import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "api/api";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation, Trans } from "react-i18next";
import styled, { keyframes } from "styled-components";
import isEmpty from "lodash.isempty";

import { Colors, FontSizes, MediaQueries, Spacing } from "@paclife/plds.tokens";
import { Button } from "@paclife/plds.button";

import SecurityIcon from "assets/svg/security.svg";
import { Toggle } from "@paclife/plds.toggle";

import { AppContext } from "context/AppContext";
import Typography from "common/Typography/Typography";
import Card from "common/Card/Card";
import { MFA_MESSAGE } from "../../constants";

import PrimaryEmail from "components/PrimaryEmail/PrimaryEmail";
import SecondaryEmail from "components/SecondaryEmail/SecondaryEmail";
import ContactNumber from "components/ContactNumber/ContactNumber";
import GoogleAuth from "components/GoogleAuth/GoogleAuth";
import InfoModal from "components/InfoModal/InfoModal";


const CustomGrid = styled.div`
    @media (min-width: ${MediaQueries.md}) {
        display: grid;
        grid-template-columns: 2fr 2.5fr;
        gap: 24px;
    }
    @media (min-width: ${MediaQueries.lg}) {
        grid-template-columns: 2fr 2fr 2fr;
    }
    margin-bottom: ${Spacing[24]};
`;

const ColumnTitle = styled(Typography)`
    margin-bottom: ${Spacing[16]};
    color: ${Colors.textSecondaryLight};
`;

const MainMessage = styled.div`
    grid-column: 1 / 3;
    margin-bottom: ${Spacing[16]};
`;

const ToggleCont = styled.div`
    margin-top: ${Spacing[24]};
`;

const ImgContainer = styled.div`
    text-align: center;
    margin-bottom: 24px;
`;

const Image = styled.img`
    width: 120px;
    // grid-column: 3 / 4;
    // grid-row: 3 / 3;
    justify-self: center;
    align-self: center;
    @media (max-width: ${MediaQueries.lg}) {
        display: none;
    }
`;

const TextButton = styled(Button)`
    font-size: ${FontSizes[400]};
`;

const shine = keyframes`
    to {
        background-position-x: -200%;
    }
`;

const Loading = styled.div`
    height: 200px;
    background: rgb(238, 238, 238);
    background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
    border-radius: 5px;
    background-size: 200% 100%;
    animation: 1.5s ${shine} linear infinite;
`;

const ButtonGroup = styled.div`
    padding-top: ${Spacing[24]};
    @media (min-width: ${MediaQueries.md}) {
        grid-column: 1 / 3;
    }
    .margin-right {
        margin-right: ${Spacing[16]};
    }
`;

const Paragraph = styled.div`
    margin-top: ${Spacing[24]};
`;

const LoginSecurity = () => {
    const { t } = useTranslation("loginSecurity");
    const { account,
            user,
            account: { factors }  
        } = useContext(AppContext);
    const mfaEnrolled = user.custom?.mfaEnrolled;
    const userType = user.custom?.type;
    const { rsdUpReadonlyaccessEn } = useFlags();

    const [enrollToggle, setEnrollToggle] = useState(false);
    const [disableTggl, setDisableTggl] = useState(false);
    const [inMFA, setInMFA] = useState(false);
    const [modal, setModal] = useState(false);
    const [isAnnDomain, setIsAnnDomain] = useState(false);

    const hostname = document.location.hostname;
    const isRiaSite = hostname.includes("ria");
    const isPfSite = hostname.includes("pacificfunds");


    useEffect(() => {
        const hostname = document.location.hostname;
        if (hostname.includes("annuities") && !hostname.includes("myaccount")) {
            setIsAnnDomain(true);
        } else {
            setIsAnnDomain(false);
        }
    }, []);

    const handleEnrollMFA = async () => {
        const { succeed } = await API.enrollMFA();
        if (succeed) {
            setInMFA(true);
            setEnrollToggle(true);
            setDisableTggl(true);
            setModal(false);
        } else {
            setEnrollToggle(false);
            setInMFA(false);
        }
    };

    const cancelModal = () => {
        if (!inMFA) {
            setModal(false); 
            setEnrollToggle(false);
        }
    };

    const faqLinks = () => {
        return (
            <>
                <a href="/home/misc/faqs/multifactorauthentication.html" 
                    target="_blank">Multi-Factor Authentication</a> and <a 
                    href="/home/misc/faqs/managemyteam.html" target="_blank">
                        Manage My Team
                </a>
            </>
        )
    }

    if (isEmpty(account)) {
        return (
            <Card title={t("title")}>
                <CustomGrid>
                <MainMessage>
                        <Typography variant="body" bold display="block">
                            {t("loginSecurity:noMfa.message")}
                        </Typography>
                        <TextButton
                            variant="text"
                            onClick={() => setModal(true)}
                        >
                            {t("loginSecurity:noMfa.link")}
                        </TextButton>
                    </MainMessage>
                    <Loading />
                    <Image src={SecurityIcon} alt="login security" />
                </CustomGrid>
                { modal && (
                    <InfoModal
                        closeModal={() => setModal(false)}
                        title={t("infoModal.title")}
                        subtitle={
                            <>
                                <Typography
                                    variant="body"
                                    align="center"
                                    display="block"
                                >
                                    <Trans i18nKey="loginSecurity:infoModal.content1" />
                                </Typography>
                                {isAnnDomain && (
                                    <Typography
                                        variant="body"
                                        align="center"
                                        display="block"
                                    >
                                        <br />
                                        <Trans i18nKey="loginSecurity:infoModal.content2" />
                                    </Typography>
                                )}
                            </>
                        }
                    />
                )}
            </Card>
        );
    }

    return (
        <Card title={t("title")}>
            <CustomGrid>
                <MainMessage>
                    <Typography variant="body" display="block">
                        { userType === 'A' && (
                                !mfaEnrolled ? 
                                    <>
                                        {t("loginSecurity:noMfa.message")}
                                        <Paragraph>
                                            <Trans i18nKey="loginSecurity:noMfa.message2">
                                                <Link to="/team" />
                                            </Trans>
                                        </Paragraph>
                                    </>
                                : t(MFA_MESSAGE)
                            )
                        }
                        { userType === 'A' &&
                            <Paragraph>
                                <div>
                                    FAQ Resources: { faqLinks() }
                                </div>
                            </Paragraph>
                        }
                        { isRiaSite || ['V', 'W', 'C'].includes(userType) ? 
                            t(MFA_MESSAGE)
                            : ''
                        }
                        { isPfSite &&
                            mfaEnrolled &&
                                t(MFA_MESSAGE)
                        }
                    </Typography>
                    { !mfaEnrolled && userType === 'A' &&
                        <ToggleCont>
                            <Toggle id="enrollToggle" 
                                label="Enroll in multi-factor authentication" 
                                disabled={disableTggl || rsdUpReadonlyaccessEn}
                                checked={enrollToggle}
                                onChange={() => setModal(true)}
                            />
                        </ToggleCont>
                    }
                  
                </MainMessage>
                <ImgContainer>
                    <Image src={SecurityIcon} alt="login security" />
                </ImgContainer>
                <div>
                    <ColumnTitle variant="h3" display="block">
                        {t("email.title")}
                    </ColumnTitle>
                    <PrimaryEmail />
                    <SecondaryEmail />
                </div>
                <div>
                    <ColumnTitle variant="h3" display="block">
                        {t("number.title")}
                    </ColumnTitle>

                    { factors !== undefined && (
                        <>
                            <ContactNumber type="phone" idx={0} />
                            <ContactNumber type="phone" idx={1} />
                        </>
                    )}
                </div>
                <div>
                    <ColumnTitle variant="h3" display="block">
                        {t("authenticator.title")}
                    </ColumnTitle>
                    <GoogleAuth type="token:software:totp" />
                </div>
            </CustomGrid>
            {modal && (
                <InfoModal
                    size={"medium"}
                    closeModal={() => setModal(false)}
                    title={t("infoModal.title")}
                    subtitle={
                        <>
                            <Typography
                                variant="body"
                                align="center"
                                display="block"
                            >
                                <Trans i18nKey="loginSecurity:infoModal.content1" />
                            </Typography>
                            {isAnnDomain && (
                                <Typography
                                    variant="body"
                                    align="center"
                                    display="block"
                                >
                                </Typography>
                            )}
                            <ButtonGroup>
                                <Button
                                    variant="secondaryNeutral"
                                    onClick={() => cancelModal()}
                                    className="margin-right"
                                    data-track="analytics"
                                    data-bname="cancel-password-update"
                                >
                                    {"Cancel"}
                                </Button>
                                <Button
                                    onClick={handleEnrollMFA}
                                    disabled={rsdUpReadonlyaccessEn}
                                    data-track="analytics"
                                    data-bname="update-password"
                                >
                                    {"Enroll"}
                                </Button>
                            </ButtonGroup>
                        </>
                    }
                />
            )}
        </Card>
    );
};

export default LoginSecurity;
